import { CircleDot, Database, Info } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { useI18n } from "@/i18n/I18nProvider";

interface ModulePlaceholderProps {
  title: string;
  subtitle: string;
  body: string;
  capabilities: string[];
  backendEndpoints: string[];
  missingEndpoints?: string[];
  testId: string;
}

export const ModulePlaceholder = ({
  title,
  subtitle,
  body,
  capabilities,
  backendEndpoints,
  missingEndpoints = [],
  testId,
}: ModulePlaceholderProps) => {
  const { t } = useI18n();

  return (
    <div data-testid={testId} className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} breadcrumb={[t.appSuffix, title]} />

      <div className="flex items-start gap-3 rounded-sm border border-border bg-[hsl(var(--surface-raised))] p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{body}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t.placeholder.notice}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="ops-panel lg:col-span-2">
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold tracking-tight">
            {t.placeholder.capabilities}
          </h2>
          <ul className="divide-y divide-border">
            {capabilities.map((item) => (
              <li key={item} className="flex items-start gap-2.5 px-4 py-2.5 text-sm">
                <CircleDot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-6">
          <div className="ops-panel">
            <h2 className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold tracking-tight">
              <Database className="h-4 w-4 text-muted-foreground" />
              {t.placeholder.backendReady}
            </h2>
            <ul className="p-4 space-y-1.5">
              {backendEndpoints.map((ep) => (
                <li key={ep} className="font-mono text-xs text-muted-foreground">
                  {ep}
                </li>
              ))}
            </ul>
          </div>

          {missingEndpoints.length > 0 && (
            <div className="ops-panel border-amber-500/40">
              <h2 className="border-b border-border px-4 py-3 text-sm font-semibold tracking-tight text-amber-700 dark:text-amber-400">
                {t.placeholder.noBackend}
              </h2>
              <ul className="p-4 space-y-1.5">
                {missingEndpoints.map((ep) => (
                  <li key={ep} className="font-mono text-xs text-muted-foreground">
                    {ep}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {backendEndpoints.length === 0 && missingEndpoints.length === 0 && (
            <div className="ops-panel">
              <EmptyState title={t.common.empty} description={t.common.emptyHint} />
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
